import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE_URL } from "@/lib/apiBase";
import { cn } from "@/lib/utils";

function dueLabel(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year:
      d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

function isOverdue(dueAt, completedAt) {
  if (!dueAt || completedAt) return false;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(d);
  dueDay.setHours(0, 0, 0, 0);
  return dueDay < today;
}

function toDateInputValue(dueAt) {
  if (!dueAt) return "";
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/**
 * Org-private personal todos on My Work (not kanban cards).
 */
export default function PersonalTasksSection({ orgId, accessToken }) {
  const [tasks, setTasks] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDue, setDraftDue] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDue, setEditDue] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/personal-tasks`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          org_id: orgId,
          ...(showCompleted ? { include_completed: "1" } : {}),
        },
      });
      setTasks(Array.isArray(res.data?.tasks) ? res.data.tasks : []);
      setOpenCount(
        Number.isFinite(res.data?.open_count) ? res.data.open_count : 0
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load personal tasks"
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, showCompleted]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const title = draftTitle.trim();
    if (!title || adding) return;
    setAdding(true);
    setError("");
    try {
      await axios.post(
        `${API_BASE_URL}/personal-tasks`,
        {
          org_id: orgId,
          title,
          ...(draftDue ? { due_at: `${draftDue}T12:00:00.000Z` } : {}),
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setDraftTitle("");
      setDraftDue("");
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to add task"
      );
    } finally {
      setAdding(false);
    }
  };

  const patchTask = async (taskId, body) => {
    setBusyId(taskId);
    setError("");
    try {
      await axios.patch(
        `${API_BASE_URL}/personal-tasks/${taskId}`,
        { org_id: orgId, ...body },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update task"
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (taskId) => {
    setBusyId(taskId);
    setError("");
    try {
      await axios.delete(`${API_BASE_URL}/personal-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
      });
      if (editingId === taskId) setEditingId(null);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to delete task"
      );
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (task) => {
    setEditingId(task.task_id);
    setEditTitle(task.title || "");
    setEditDue(toDateInputValue(task.due_at));
  };

  const saveEdit = async (taskId) => {
    const title = editTitle.trim();
    if (!title) return;
    await patchTask(taskId, {
      title,
      due_at: editDue ? `${editDue}T12:00:00.000Z` : null,
    });
    setEditingId(null);
  };

  const openTasks = tasks.filter((t) => !t.completed_at);
  const completedTasks = tasks.filter((t) => t.completed_at);

  return (
    <section className="rounded-lg border border-border bg-background">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-sm font-semibold text-foreground">
            Personal tasks
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {openCount}
          </span>
        </div>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          onClick={() => setShowCompleted((v) => !v)}
        >
          {showCompleted ? "Hide completed" : "Show completed"}
        </button>
      </div>

      <div className="border-t border-border px-4 py-3 space-y-3">
        <form
          onSubmit={handleAdd}
          className="flex flex-col gap-2 sm:flex-row sm:items-center"
        >
          <Input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Add a personal reminder…"
            className="h-9 flex-1"
            maxLength={200}
            disabled={adding}
          />
          <Input
            type="date"
            value={draftDue}
            onChange={(e) => setDraftDue(e.target.value)}
            className="h-9 w-full sm:w-40"
            disabled={adding}
            title="Optional due date"
          />
          <Button
            type="submit"
            size="sm"
            className="h-9"
            disabled={adding || !draftTitle.trim()}
          >
            {adding ? "Adding…" : "Add"}
          </Button>
        </form>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : openTasks.length === 0 && completedTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Private reminders for this workspace — not on any board.
          </p>
        ) : (
          <ul className="space-y-1">
            {openTasks.map((task) => {
              const overdue = isOverdue(task.due_at, task.completed_at);
              const due = dueLabel(task.due_at);
              const busy = busyId === task.task_id;
              const editing = editingId === task.task_id;
              return (
                <li
                  key={task.task_id}
                  className="flex items-start gap-2 rounded-md px-1 py-1.5 hover:bg-muted/40"
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      patchTask(task.task_id, { completed: true })
                    }
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border text-transparent hover:border-foreground hover:text-muted-foreground"
                    aria-label={`Complete ${task.title}`}
                    title="Mark complete"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <div className="min-w-0 flex-1">
                    {editing ? (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="h-8 flex-1"
                          maxLength={200}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void saveEdit(task.task_id);
                            }
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <Input
                          type="date"
                          value={editDue}
                          onChange={(e) => setEditDue(e.target.value)}
                          className="h-8 w-full sm:w-36"
                        />
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            className="h-8"
                            disabled={busy || !editTitle.trim()}
                            onClick={() => saveEdit(task.task_id)}
                          >
                            Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <button
                          type="button"
                          className="text-sm text-left text-foreground hover:underline"
                          onClick={() => startEdit(task)}
                        >
                          {task.title}
                        </button>
                        {due ? (
                          <span
                            className={cn(
                              "text-xs tabular-nums",
                              overdue
                                ? "text-destructive"
                                : "text-muted-foreground"
                            )}
                            title={overdue ? "Overdue" : "Due date"}
                          >
                            {due}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {!editing ? (
                    <div className="flex shrink-0 gap-0.5">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => startEdit(task)}
                        className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleDelete(task.task_id)}
                        className="rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-muted"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
            {showCompleted &&
              completedTasks.map((task) => (
                <li
                  key={task.task_id}
                  className="flex items-start gap-2 rounded-md px-1 py-1.5 opacity-70"
                >
                  <button
                    type="button"
                    disabled={busyId === task.task_id}
                    onClick={() =>
                      patchTask(task.task_id, { completed: false })
                    }
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border bg-muted text-foreground"
                    aria-label={`Reopen ${task.title}`}
                    title="Mark incomplete"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <span className="min-w-0 flex-1 text-sm text-muted-foreground line-through">
                    {task.title}
                  </span>
                  <button
                    type="button"
                    disabled={busyId === task.task_id}
                    onClick={() => handleDelete(task.task_id)}
                    className="rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-muted"
                    aria-label="Delete"
                    title="Delete"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </section>
  );
}
